import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Modal, Pressable, TextInput, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { DiscoveryFilters, DiscoverySort } from '@/types/discovery';
import { cn } from '@/utils/cn';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

const SORT_OPTIONS: { value: DiscoverySort; label: string }[] = [
  { value: 'relevance', label: 'Relevância' },
  { value: 'activity', label: 'Mais ativos' },
  { value: 'price_asc', label: 'Menor preço' },
];

type SearchFiltersSheetProps = {
  open: boolean;
  filters: DiscoveryFilters;
  onClose: () => void;
  onApply: (filters: DiscoveryFilters) => void;
};

function parsePrice(value: string): number | undefined {
  const parsed = Number(value.replace(/[^\d]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function SearchFiltersSheet({ open, filters, onClose, onApply }: SearchFiltersSheetProps) {
  const [draft, setDraft] = useState<DiscoveryFilters>(filters);
  const [minPrice, setMinPrice] = useState(filters.minPrice?.toString() ?? '');
  const [maxPrice, setMaxPrice] = useState(filters.maxPrice?.toString() ?? '');

  const handleApply = () => {
    onApply({
      ...draft,
      minPrice: parsePrice(minPrice),
      maxPrice: parsePrice(maxPrice),
    });
    onClose();
  };

  const handleClear = () => {
    setDraft({ q: filters.q, categories: filters.categories });
    setMinPrice('');
    setMaxPrice('');
  };

  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable className="flex-1 justify-end bg-black/40" onPress={onClose}>
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="rounded-t-3xl bg-card"
          style={{ borderCurve: 'continuous' }}
        >
          <View className="items-center pt-3">
            <View className="h-1 w-10 rounded-full bg-neutral-200" />
          </View>

          <KeyboardAwareScrollView
            bottomOffset={KEYBOARD_BOTTOM_OFFSET}
            className="max-h-[70vh]"
            keyboardShouldPersistTaps="handled"
          >
            <View className="gap-5 p-5">
              <View className="flex-row items-center justify-between">
                <ThemedText className="font-bold text-lg text-foreground">Filtros</ThemedText>

                <Pressable onPress={handleClear} hitSlop={8}>
                  <ThemedText className="font-semibold text-sm text-primary">Limpar</ThemedText>
                </Pressable>
              </View>

              <View className="gap-2">
                <ThemedText className="font-semibold text-sm text-foreground">
                  Ordenar por
                </ThemedText>

                <View className="flex-row flex-wrap gap-2">
                  {SORT_OPTIONS.map((option) => {
                    const isSelected = (draft.sort ?? 'relevance') === option.value;

                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => setDraft({ ...draft, sort: option.value })}
                        className={cn(
                          'rounded-full border px-3 py-1.5',
                          isSelected
                            ? 'border-primary bg-primary-50'
                            : 'border-neutral-900/5 bg-neutral-50',
                        )}
                      >
                        <ThemedText
                          className={cn(
                            'text-xs',
                            isSelected ? 'font-semibold text-primary' : 'text-neutral-600',
                          )}
                        >
                          {option.label}
                        </ThemedText>
                      </Pressable>
                    );
                  })}
                </View>
              </View>

              <View className="gap-2">
                <ThemedText className="font-semibold text-sm text-foreground">
                  Faixa de preço
                </ThemedText>

                <View className="flex-row items-center gap-3">
                  <View className="h-11 flex-1 flex-row items-center gap-1 rounded-full bg-neutral-100 px-4">
                    <ThemedText className="text-xs text-neutral-400">R$</ThemedText>
                    <TextInput
                      value={minPrice}
                      onChangeText={setMinPrice}
                      placeholder="mín."
                      keyboardType="numeric"
                      placeholderTextColor="#A3A3A3"
                      className="flex-1 text-foreground"
                    />
                  </View>

                  <View className="h-11 flex-1 flex-row items-center gap-1 rounded-full bg-neutral-100 px-4">
                    <ThemedText className="text-xs text-neutral-400">R$</ThemedText>
                    <TextInput
                      value={maxPrice}
                      onChangeText={setMaxPrice}
                      placeholder="máx."
                      keyboardType="numeric"
                      placeholderTextColor="#A3A3A3"
                      className="flex-1 text-foreground"
                    />
                  </View>
                </View>
              </View>

              <View className="gap-3">
                <View className="flex-row items-center justify-between">
                  <View className="flex-1 gap-0.5 pr-3">
                    <ThemedText className="font-semibold text-sm text-foreground">
                      Aceita agendamento online
                    </ThemedText>
                    <ThemedText className="text-xs text-neutral-500">
                      Só quem tem agenda ativa e horário para marcar
                    </ThemedText>
                  </View>

                  <Switch
                    checked={Boolean(draft.bookableOnly)}
                    onCheckedChange={(value) => setDraft({ ...draft, bookableOnly: value })}
                  />
                </View>

                <View className="flex-row items-center justify-between">
                  <View className="flex-1 gap-0.5 pr-3">
                    <ThemedText className="font-semibold text-sm text-foreground">
                      Tem fotos de trabalhos
                    </ThemedText>
                    <ThemedText className="text-xs text-neutral-500">
                      Perfis com galeria preenchida
                    </ThemedText>
                  </View>

                  <Switch
                    checked={Boolean(draft.hasGallery)}
                    onCheckedChange={(value) => setDraft({ ...draft, hasGallery: value })}
                  />
                </View>
              </View>

              <Button onPress={handleApply} className="w-full">
                <View className="flex-row items-center gap-2">
                  <Ionicons name="funnel" size={15} color="#fff" />
                  <ThemedText className="font-semibold text-white">Aplicar filtros</ThemedText>
                </View>
              </Button>
            </View>
          </KeyboardAwareScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
